import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, type DirEntry, type Me, type ShareMeta } from '../lib/api'
import { useRouter } from '../lib/router'
import { Icon } from '../components/Icon'
import { EmptyState, ErrorState, PermissionState, SkeletonList } from '../components/StateBlock'
import { FileBrowser } from '../components/browser/FileBrowser'
import { PreviewViewer } from '../components/preview/PreviewViewer'
import { bisaDipratinjau, sumberShare } from '../components/browser/source'
import { toast, uiPrompt } from '../components/overlay'
import { DropZone, UploadPanel } from '../components/upload/UploadPanel'
import { useUploader } from '../components/upload/useUploader'
import { Button } from '../components/Button'
import css from './SharePage.module.css'

/**
 * Halaman share link.
 *
 * Permukaan yang paling banyak dilihat orang luar dan mayoritas dibuka dari
 * ponsel. Di versi lama, setiap keadaan akses berakhir sebagai halaman
 * die() teks hitam-putih tanpa merek dan tanpa jalan keluar. Di sini tiap
 * keadaan menjadi halaman bermerek dengan satu tindakan yang jelas.
 */

type Alasan = 'need_login' | 'need_request' | 'pending' | 'rejected' | 'no_nij' | 'expired'

interface Keadaan {
  memuat: boolean
  meta?: ShareMeta
  alasan?: Alasan
  galat?: string
  hilang?: boolean
}

export function SharePage({ token }: { token: string }) {
  const { search, setQuery, navigate } = useRouter()
  const [keadaan, setKeadaan] = useState<Keadaan>({ memuat: true })
  const [saya, setSaya] = useState<Me | null>(null)
  const [penyegar, setPenyegar] = useState(0)
  const inputBerkas = useRef<HTMLInputElement>(null)
  const unggah = useUploader(() => setPenyegar((n) => n + 1))

  const dir = search.get('dir') ?? ''
  const pratinjauPath = search.get('preview')

  // Identitas dipakai untuk memutuskan apakah tombol "Minta akses" atau
  // "Masuk" yang ditampilkan. Gagal berarti belum masuk — bukan galat.
  useEffect(() => {
    const ac = new AbortController()
    api
      .me(ac.signal)
      .then(setSaya)
      .catch(() => setSaya(null))
    return () => ac.abort()
  }, [])

  const muat = useCallback(() => {
    const ac = new AbortController()
    setKeadaan({ memuat: true })

    api
      .share
      .meta(token, ac.signal)
      .then((meta) => setKeadaan({ memuat: false, meta }))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (err instanceof ApiError) {
          const alasan = (err.body as { reason?: Alasan } | undefined)?.reason
          if (err.tidakDitemukan && !alasan) {
            setKeadaan({ memuat: false, hilang: true })
            return
          }
          setKeadaan({ memuat: false, ...(alasan ? { alasan } : { galat: err.message }) })
          return
        }
        setKeadaan({ memuat: false, galat: 'Tidak dapat menghubungi server.' })
      })

    return () => ac.abort()
  }, [token])

  useEffect(() => muat(), [muat, penyegar])

  const sumber = useMemo(
    () => sumberShare(token, keadaan.meta?.permission === 'write', saya !== null),
    [token, keadaan.meta?.permission, saya],
  )

  // Daftar berkas yang sedang tampil, dipertahankan agar penampil pratinjau
  // tahu urutan maju-mundurnya persis seperti yang dilihat pengguna.
  const [daftarTampil, setDaftarTampil] = useState<DirEntry[]>([])
  const itemPratinjau = useMemo(
    () => (pratinjauPath ? daftarTampil.find((d) => d.path === pratinjauPath) : undefined),
    [pratinjauPath, daftarTampil],
  )

  const mintaAkses = async () => {
    const pesan = await uiPrompt({
      title: 'Minta akses ke folder ini',
      description:
        'Pemilik tautan akan menerima permintaan Anda dan dapat menyetujuinya. Anda boleh menambahkan keterangan singkat.',
      label: 'Keterangan (opsional)',
      placeholder: 'Misalnya: untuk keperluan dokumentasi ibadah 3 Agustus',
      confirmLabel: 'Kirim permintaan',
    })
    if (pesan === null) return

    try {
      await api.share.mintaAkses(token, pesan)
      toast.success('Permintaan akses terkirim', {
        detail: 'Anda akan bisa membuka tautan ini setelah disetujui.',
      })
      setPenyegar((n) => n + 1)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Permintaan gagal dikirim.')
    }
  }

  const keLogin = () => {
    const kembali = window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(kembali)}`
  }

  // ── Keadaan ──────────────────────────────────────────────────────────────

  if (keadaan.memuat) {
    return (
      <Kerangka>
        <div className={css.panel}>
          <SkeletonList rows={6} />
        </div>
      </Kerangka>
    )
  }

  if (keadaan.hilang) {
    return (
      <Kerangka>
        <EmptyState
          icon="link"
          title="Tautan tidak ditemukan"
          message="Tautan ini sudah dihapus, atau alamatnya tidak lengkap. Coba minta tautan baru kepada orang yang membagikannya."
          action={
            <Button variant="secondary" onClick={() => navigate('/')}>
              Ke halaman utama
            </Button>
          }
        />
      </Kerangka>
    )
  }

  if (keadaan.galat) {
    return (
      <Kerangka>
        <ErrorState message={keadaan.galat} onRetry={() => setPenyegar((n) => n + 1)} />
      </Kerangka>
    )
  }

  if (keadaan.alasan) {
    return (
      <Kerangka>
        <KeadaanAkses
          alasan={keadaan.alasan}
          sudahMasuk={saya !== null}
          punyaNij={Boolean(saya?.nij)}
          onMasuk={keLogin}
          onMinta={mintaAkses}
        />
      </Kerangka>
    )
  }

  const meta = keadaan.meta
  if (!meta) return null

  if (!meta.exists) {
    return (
      <Kerangka judul={meta.label ?? meta.name}>
        <EmptyState
          icon="warning"
          title="Berkas sudah tidak ada"
          message="Tautannya masih berlaku, tetapi berkas atau folder yang dituju sudah dipindahkan atau dihapus dari penyimpanan."
        />
      </Kerangka>
    )
  }

  // ── Berkas tunggal ───────────────────────────────────────────────────────
  if (!meta.is_folder) {
    return (
      <Kerangka judul={meta.label ?? meta.name}>
        <div className={css.singleCard}>
          <div className={css.singleIcon}>
            <Icon name="file" size={34} />
          </div>
          <h2 className={css.singleName}>{meta.name}</h2>
          {meta.label && meta.label !== meta.name && (
            <p className={css.singleLabel}>{meta.label}</p>
          )}
          <div className={css.singleActions}>
            <Button
              variant="primary"
              size="lg"
              icon="download"
              onClick={() => {
                window.location.href = sumber.urlUnduh('')
              }}
            >
              Unduh berkas
            </Button>
          </div>
          <p className={css.singleHint}>
            Unduhan dapat dilanjutkan bila terputus, dan dapat dipakai dengan pengelola unduhan.
          </p>
        </div>
      </Kerangka>
    )
  }

  // ── Folder ───────────────────────────────────────────────────────────────
  const bolehUnggah = sumber.kemampuan.bolehUnggah
  const targetUnggah = { upload_type: 'shared' as const, token, target_dir: dir }

  return (
    <Kerangka judul={meta.label ?? meta.name} lebar>
      {bolehUnggah && (
        <div className={css.unggahBar}>
          <Icon name="upload" size={16} />
          <span className={css.unggahTeks}>
            Folder ini terbuka untuk unggahan. Berkas yang Anda kirim dihitung ke kuota akun
            Anda sendiri, bukan kuota pemilik tautan.
          </span>
          <Button variant="primary" size="sm" icon="upload" onClick={() => inputBerkas.current?.click()}>
            Unggah berkas
          </Button>
          <input
            ref={inputBerkas}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) unggah.tambah(files, targetUnggah)
              e.target.value = ''
            }}
          />
        </div>
      )}

      <DropZone aktif={bolehUnggah} onFiles={(files) => unggah.tambah(files, targetUnggah)}>
        <FileBrowser
          sumber={sumber}
          dir={dir}
          onPindahDir={(d) => setQuery({ dir: d || null, preview: null }, { replace: false })}
          onPratinjau={(item) => setQuery({ preview: item.path }, { replace: false })}
          onDaftarTampil={setDaftarTampil}
          onSeleksiBerubah={() => {}}
          penyegar={penyegar}
        />
      </DropZone>

      <UploadPanel
        antrean={unggah.antrean}
        onBatal={unggah.batalkan}
        onBersihkan={unggah.bersihkan}
        adaYangBerjalan={unggah.adaYangBerjalan}
      />

      {itemPratinjau && (
        <PreviewViewer
          sumber={sumber}
          item={itemPratinjau}
          daftar={daftarTampil.filter(bisaDipratinjau)}
          onTutup={() => setQuery({ preview: null }, { replace: false })}
          onPindah={(it) => setQuery({ preview: it.path }, { replace: true })}
        />
      )}
    </Kerangka>
  )
}

/** Kerangka halaman publik — tanpa sidebar aplikasi. */
function Kerangka({
  judul,
  lebar,
  children,
}: {
  judul?: string
  lebar?: boolean
  children: React.ReactNode
}) {
  useEffect(() => {
    if (judul) document.title = `${judul} — GMS Drive`
  }, [judul])

  return (
    <div className={css.page}>
      <header className={css.header}>
        <a className={css.brand} href="/">
          <span className={css.brandMark}>
            <Icon name="drive" size={18} />
          </span>
          GMS Drive
        </a>
      </header>

      <main className={`${css.content} ${lebar ? css.wide : ''}`}>
        {judul && <h1 className={css.title}>{judul}</h1>}
        {children}
      </main>

      <footer className={css.footer}>Berbagi berkas internal GMS Purwokerto</footer>
    </div>
  )
}

function KeadaanAkses({
  alasan,
  sudahMasuk,
  punyaNij,
  onMasuk,
  onMinta,
}: {
  alasan: Alasan
  sudahMasuk: boolean
  punyaNij: boolean
  onMasuk: () => void
  onMinta: () => void
}) {
  switch (alasan) {
    case 'need_login':
      return (
        <PermissionState
          title="Tautan ini hanya untuk pengguna terdaftar"
          message="Masuk dengan akun MyGMS Anda untuk membuka berkas yang dibagikan."
          action={
            <Button variant="primary" size="lg" icon="logout" onClick={onMasuk}>
              Masuk dengan MyGMS
            </Button>
          }
        />
      )

    case 'no_nij':
      return (
        <PermissionState
          title="Akun Anda belum memiliki NIJ"
          message="Tautan ini dibatasi untuk NIJ tertentu, sehingga akses Anda tidak dapat diverifikasi. Silakan hubungi admin agar NIJ Anda dilengkapi terlebih dahulu."
        />
      )

    case 'need_request':
      return (
        <PermissionState
          title="Anda perlu meminta akses"
          message="Tautan ini dibatasi untuk orang tertentu. Kirim permintaan, dan pemilik tautan akan meninjaunya."
          action={
            sudahMasuk ? (
              <Button variant="primary" size="lg" icon="inbox" onClick={onMinta} disabled={!punyaNij}>
                Minta akses
              </Button>
            ) : (
              <Button variant="primary" size="lg" onClick={onMasuk}>
                Masuk dulu
              </Button>
            )
          }
        />
      )

    case 'pending':
      return (
        <PermissionState
          title="Permintaan Anda sedang ditinjau"
          message="Pemilik tautan sudah menerima permintaan Anda. Silakan periksa kembali beberapa saat lagi."
          action={
            <Button variant="secondary" icon="refresh" onClick={() => window.location.reload()}>
              Periksa lagi
            </Button>
          }
        />
      )

    case 'rejected':
      return (
        <PermissionState
          title="Permintaan Anda tidak disetujui"
          message="Pemilik tautan belum memberikan akses. Anda dapat mengirim permintaan baru dengan keterangan yang lebih jelas."
          action={
            <Button variant="secondary" icon="inbox" onClick={onMinta}>
              Kirim permintaan lagi
            </Button>
          }
        />
      )

    case 'expired':
      return (
        <EmptyState
          icon="clock"
          title="Tautan ini sudah kedaluwarsa"
          message="Masa berlaku tautan sudah habis. Silakan minta tautan baru kepada orang yang membagikannya."
        />
      )
  }
}
